import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ChatMessage } from '../types';

interface ChatOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    fileId: string;
    meetingType: string;
    templateUuid?: string | null;
    messages: ChatMessage[];
    onMessagesChange: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    onAddToSummary?: (message: string) => Promise<void>;
    canAddToSummary?: boolean;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({
    isOpen,
    onClose,
    fileId,
    meetingType,
    templateUuid,
    messages,
    onMessagesChange,
    onAddToSummary,
    canAddToSummary = false,
}) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [copyAllSuccess, setCopyAllSuccess] = useState(false);
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const apiService = useMemo(() => new ApiService(), []);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timer = window.setTimeout(() => {
            textareaRef.current?.focus();
        }, 150);

        return () => window.clearTimeout(timer);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                handleClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleClose]);

    const appendMessage = (message: ChatMessage) => {
        onMessagesChange(prev => [...prev, message]);
    };

    const updateMessage = (id: string, partial: Partial<ChatMessage>) => {
        onMessagesChange(prev => prev.map(message => (
            message.id === id ? { ...message, ...partial } : message
        )));
    };

    const formatTimestamp = (date: Date) => {
        try {
            return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    const buildConversationTranscript = () => {
        return messages.map(message => {
            if (message.role === 'user') {
                return t('chatOverlay.transcript.user', { content: message.content });
            }
            if (message.role === 'assistant') {
                return t('chatOverlay.transcript.assistant', { content: message.content });
            }
            return t('chatOverlay.transcript.system', { content: message.content });
        }).join('\n\n');
    };

    const deriveErrorMessage = (error: unknown): string => {
        if (typeof error === 'string') {
            return error;
        }
        if (error instanceof Error) {
            return error.message;
        }
        if (error && typeof error === 'object' && 'message' in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }
        return t('chatOverlay.errors.generic');
    };

    const handleSend = async () => {
        const trimmedValue = inputValue.trim();
        if (!trimmedValue || isSending) {
            return;
        }

        appendMessage({
            id: `user-${Date.now()}`,
            role: 'user',
            content: trimmedValue,
            timestamp: new Date(),
            status: 'delivered',
        });

        setInputValue('');
        setIsSending(true);

        try {
            const response = await apiService.sendChatMessage(fileId, { meetingType, templateUuid }, trimmedValue);
            const assistantMessage: ChatMessage = {
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: response || t('chatOverlay.noResponse'),
                timestamp: new Date(),
                canAddToSummary: Boolean(onAddToSummary && canAddToSummary),
                status: 'delivered',
            };
            appendMessage(assistantMessage);
        } catch (error) {
            const errorMessage = deriveErrorMessage(error);
            appendMessage({
                id: `system-${Date.now()}`,
                role: 'system',
                content: `⚠️ ${errorMessage}`,
                timestamp: new Date(),
                status: 'error',
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleCopyConversation = async () => {
        if (!messages.length) {
            return;
        }

        try {
            await navigator.clipboard.writeText(buildConversationTranscript());
            setCopyAllSuccess(true);
            window.setTimeout(() => setCopyAllSuccess(false), 2000);
        } catch (error) {
            const errorMessage = deriveErrorMessage(error);
            appendMessage({
                id: `system-${Date.now()}`,
                role: 'system',
                content: `⚠️ ${t('chatOverlay.errors.copyConversation', { error: errorMessage })}`,
                timestamp: new Date(),
                status: 'error',
            });
        }
    };

    const handleCopyMessage = async (message: ChatMessage) => {
        try {
            await navigator.clipboard.writeText(message.content);
            setCopiedMessageId(message.id);
            window.setTimeout(() => setCopiedMessageId(null), 1500);
        } catch (error) {
            const errorMessage = deriveErrorMessage(error);
            appendMessage({
                id: `system-${Date.now()}`,
                role: 'system',
                content: `⚠️ ${t('chatOverlay.errors.copyMessage', { error: errorMessage })}`,
                timestamp: new Date(),
                status: 'error',
            });
        }
    };

    const handleAddToSummary = async (message: ChatMessage) => {
        if (!onAddToSummary || !canAddToSummary || message.addedToSummary || message.isAddingToSummary) {
            return;
        }

        updateMessage(message.id, { isAddingToSummary: true });

        try {
            await onAddToSummary(message.content);
            updateMessage(message.id, {
                isAddingToSummary: false,
                addedToSummary: true,
            });
        } catch (error) {
            const errorMessage = deriveErrorMessage(error);
            appendMessage({
                id: `system-${Date.now()}`,
                role: 'system',
                content: `⚠️ ${t('chatOverlay.errors.addToSummary', { error: errorMessage })}`,
                timestamp: new Date(),
                status: 'error',
            });
        } finally {
            // Ensure pending state clears, ook bij fouten
            updateMessage(message.id, { isAddingToSummary: false });
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            ref={overlayRef}
            role="dialog"
            aria-modal="false"
            aria-label={t('chatOverlay.ariaLabel')}
            className="chat-dock"
            onMouseDown={event => event.stopPropagation()}
        >
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">{t('chatOverlay.title')}</h2>
                            <p className="text-xs text-gray-500 mt-0.5">{t('chatOverlay.subtitle')}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleCopyConversation}
                                disabled={!messages.length}
                                className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={copyAllSuccess ? t('common.copied') : t('chatOverlay.copyConversation')}
                            >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16h8M8 12h8m-8-4h8M6 4h10a2 2 0 012 2v12a2 2 0 01-2 2H6l-4 4V6a2 2 0 012-2h2z" />
                                </svg>
                                {copyAllSuccess ? t('chatOverlay.copiedConversationButton') : t('chatOverlay.copyConversationButton')}
                            </button>
                            <button
                                onClick={handleClose}
                                className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                                title={t('common.close')}
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                        {messages.length === 0 && (
                            <div className="h-full flex flex-col items-center justify-center text-center text-sm text-gray-500 py-12">
                                <div className="w-12 h-12 rounded-full bg-primary-50 text-primary-500 flex items-center justify-center mb-3">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m-2 8a9 9 0 100-18 9 9 0 000 18z" />
                                    </svg>
                                </div>
                                <p className="font-medium text-gray-700 mb-1">{t('chatOverlay.empty.title')}</p>
                                <p>{t('chatOverlay.empty.body')}</p>
                            </div>
                        )}

                        {messages.map(message => {
                            if (message.role === 'system') {
                                return (
                                    <div key={message.id} className="flex justify-center">
                                        <div className="max-w-xs rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-700">
                                            <span className="block whitespace-pre-line">{message.content}</span>
                                        </div>
                                    </div>
                                );
                            }

                            const isUser = message.role === 'user';
                            const alignmentClass = isUser ? 'justify-end' : 'justify-start';
                            const bubbleClass = isUser
                                ? 'bg-primary-600 text-white rounded-2xl rounded-br-md'
                                : 'bg-gray-50 text-gray-900 border border-gray-200 rounded-2xl rounded-bl-md';

                            return (
                                <div key={message.id} className={`flex ${alignmentClass}`}>
                                    <div className="max-w-[80%] space-y-1">
                                        <div className={`px-4 py-2 text-sm leading-relaxed shadow-sm ${bubbleClass}`}>
                                            <p className="whitespace-pre-line break-words">{message.content}</p>
                                        </div>
                                        <div className={`flex items-center text-[11px] uppercase tracking-wide text-gray-400 ${isUser ? 'justify-end' : 'justify-start'} gap-2`}>
                                            <span>{isUser ? t('chatOverlay.sender.you') : t('chatOverlay.sender.assistant')} • {formatTimestamp(message.timestamp)}</span>
                                            {!isUser && canAddToSummary && message.canAddToSummary && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleCopyMessage(message)}
                                                        className="text-gray-400 hover:text-gray-600 transition"
                                                        title={copiedMessageId === message.id ? t('common.copied') : t('chatOverlay.copyMessage')}
                                                    >
                                                        {copiedMessageId === message.id ? (
                                                            <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h6a2 2 0 012 2v8a2 2 0 01-2 2h-6a2 2 0 01-2-2V9a2 2 0 012-2zm0 0V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                    {message.addedToSummary ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-600 border border-emerald-200">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
                                                            </svg>
                                                            {t('chatOverlay.added')}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleAddToSummary(message)}
                                                            className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-2 py-1 text-[10px] font-semibold text-primary-600 hover:bg-primary-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                            disabled={message.isAddingToSummary}
                                                        >
                                                            {message.isAddingToSummary ? (
                                                                <>
                                                                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V1.5C5.648 1.5 1.5 5.648 1.5 11H4z" />
                                                                    </svg>
                                                                    {t('chatOverlay.addingToSummary')}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v12m0 0l4-4m-4 4l-4-4m-6 6h16" />
                                                                    </svg>
                                                                    {t('chatOverlay.addToSummary')}
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {isSending && (
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                <div className="flex space-x-1">
                                    <div className="w-2 h-2 rounded-full bg-primary-400 animate-bounce" />
                                    <div className="w-2 h-2 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                                    <div className="w-2 h-2 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                                </div>
                                <span>{t('chatOverlay.thinking')}</span>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100 transition">
                            <textarea
                                ref={textareaRef}
                                rows={3}
                                value={inputValue}
                                onChange={event => setInputValue(event.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('chatOverlay.inputPlaceholder')}
                                className="w-full resize-none rounded-xl border-0 bg-transparent px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
                                disabled={isSending}
                            />
                            <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
                                <span className="text-[11px] uppercase tracking-wide text-gray-400">
                                    {/* Enter om te versturen • Shift + Enter voor nieuwe regel */}
                                </span>
                                <button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || isSending}
                                    className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    {isSending ? (
                                        <>
                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            {t('chatOverlay.sending')}
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2z" />
                                            </svg>
                                            {t('chatOverlay.send')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
        </div>
    );
};

export default ChatOverlay;
